import React, { useEffect, useState } from "react";
import PanelShell from "../../shared/PanelShell";
import { Skeleton } from "../../shared/Skeleton";
import { fmtCurrency, fmtDateLabel, fmtNumber, telHref, titleCase } from "../../shared/format";
import { drill as fnDrill } from "../../../api/conversation";
import { ROW_HOVER_FILL, ROW_LIST_BLEED } from "./rowStyles";

// IN PROGRESS — Addendum 1 §3, Tier 3, beside Follow-ups. The people who built a
// reservation in Campspot and did not pay. A stronger worklist than the web
// abandoned cart: the agent already knows exactly what they wanted — a real cart,
// an amount, and a phone number to call them on.
//
// SELF-FETCHES its rows, because reservation rows come from fn_drill, not a
// load-time RPC (fn_get_conversation_bookings carries only the counts). It fires
// fn_drill('reservation','unpaid', scope, 50) on mount, keyed on client / range /
// modality — the WebsiteGapsPanel self-fetch pattern.
//
// NO SENTENCE. The money-strip "In progress" cell carries the takeaway; a second
// say-map string here would say the same thing twice. So this panel has no
// panel_key in the say map and PanelShell renders no takeaway.
//
// checkout_url IS NULL ON EVERY UNPAID ROW — the checkout function writes the link
// only after payment, so the people who need it are the only ones without it (a
// known backend gap, not ours). The affordance is rendered only when the link is
// present and is plain text otherwise — never a dead link. Same link-or-span gate
// FollowupsPanel uses for its tel: identifier.
const PREVIEW = 5;
const FETCH_LIMIT = 50;

// One reservation, described the way a person reads it: who, what they were
// booking, when, and what it is worth. site_name + site_number, the stay dates,
// and the night count — each dropped when absent rather than rendered as an empty
// fragment.
const metaLine = (row) => {
  const site = [row.site_name, row.site_number ? `site ${row.site_number}` : null]
    .filter(Boolean)
    .join(", ");
  const dates = row.check_in && row.check_out
    ? `${fmtDateLabel(row.check_in)} – ${fmtDateLabel(row.check_out)}`
    : null;
  const nights = row.nights != null ? `${row.nights} ${row.nights === 1 ? "night" : "nights"}` : null;
  return [site || null, dates, nights].filter(Boolean).join(" · ");
};

const InProgressPanel = ({ panelKey, clientId, range, modality = "voice", inProgress = null }) => {
  const start = range?.start;
  const end = range?.end;

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!clientId || !start || !end) return undefined;
    let cancelled = false;
    setLoading(true);
    setExpanded(false);
    const scope = { client_id: clientId, start_date: start, end_date: end, modality };
    fnDrill("reservation", "unpaid", scope, FETCH_LIMIT)
      .then((res) => { if (!cancelled) setRows(Array.isArray(res?.rows) ? res.rows : []); })
      .catch((e) => { console.error("in_progress:", e); if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, start, end, modality]);

  const items = Array.isArray(rows) ? rows : [];
  const shown = expanded ? items : items.slice(0, PREVIEW);
  const hidden = items.length - shown.length;

  return (
    <PanelShell
      panelKey={panelKey}
      label="In progress"
      say={{}}
      hasData={items.length > 0 || (inProgress?.count ?? 0) > 0}
      loading={loading}
      emptyMessage="No unpaid reservations — everything the agent built was paid for."
      className="flex flex-col"
    >
      {/* Authoritative headline (fn_get_conversation_bookings) so this card and the
          money-strip "In progress" cell show the SAME count/value (Markup #11). The
          rows below come from fn_drill; if they don't sum to this, that gap is a
          backend reconciliation between the aggregate and the drill, flagged
          separately — not something the panel resolves. */}
      {inProgress?.count != null && (
        <p data-testid="in-progress-total" className="gi-body text-gray-600 dark:text-gray-400 mb-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {fmtNumber(inProgress.count)} {inProgress.count === 1 ? "person" : "people"}
          </span>
          {inProgress.value != null && <> · worth {fmtCurrency(inProgress.value)}</>}
        </p>
      )}
      {loading && rows == null ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : (
        <ul className={`divide-y divide-gray-200 dark:divide-gray-800 flex-1 min-h-0 max-h-[30rem] overflow-y-auto overflow-x-hidden ${ROW_LIST_BLEED}`}>
          {shown.map((row, i) => {
            const name = titleCase(row.guest_name) || "Guest";
            // checkout_url present → the name is the link to complete the cart;
            // absent → plain text. NO dead link.
            const checkout = row.checkout_url || null;
            const phoneHref = telHref(row.phone);
            const meta = metaLine(row);
            return (
              <li key={row.reservation_id || row.conversation_id || i} className={`py-3 flex flex-col gap-1 ${ROW_HOVER_FILL}`}>
                <div className="flex items-start justify-between gap-2 min-w-0">
                  {checkout ? (
                    <a
                      href={checkout}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="in-progress-checkout"
                      className="gi-body font-medium inline-flex items-center max-md:min-h-[44px] min-w-0 break-words gi-link transition-colors no-underline"
                    >
                      {name}
                    </a>
                  ) : (
                    <span className="gi-body font-medium min-w-0 break-words text-gray-900 dark:text-gray-100">{name}</span>
                  )}
                  {row.total != null && (
                    <span className="gi-label shrink-0 text-gray-500 dark:text-gray-400 tabular-nums">{fmtCurrency(row.total)}</span>
                  )}
                </div>
                {meta && <p className="gi-body break-words text-gray-600 dark:text-gray-400">{meta}</p>}
                {/* The phone number to call them back on — tappable when it is one,
                    plain text otherwise. This is a call-back worklist. */}
                {row.phone && (phoneHref ? (
                  <a href={phoneHref} className="gi-body inline-flex items-center max-md:min-h-[44px] gi-link transition-colors no-underline">
                    {row.phone}
                  </a>
                ) : (
                  <span className="gi-body text-gray-500 dark:text-gray-400">{row.phone}</span>
                ))}
              </li>
            );
          })}
        </ul>
      )}
      {hidden > 0 && (
        <button
          type="button"
          data-testid="in-progress-expand"
          onClick={() => setExpanded(true)}
          className="mt-2 inline-flex items-center min-h-[44px] text-sm font-medium gi-link transition-colors"
        >
          {`Show ${hidden.toLocaleString()} more`}
        </button>
      )}
      {expanded && items.length > PREVIEW && (
        <button
          type="button"
          data-testid="in-progress-collapse"
          onClick={() => setExpanded(false)}
          className="mt-2 inline-flex items-center min-h-[44px] text-sm font-medium gi-link transition-colors"
        >
          Show fewer
        </button>
      )}
    </PanelShell>
  );
};

export default InProgressPanel;
